import { useState } from 'react';

import { useCrowdfundingProvider } from '#Context/crowdfunding/Crowdfunding.provider.js';
import { useSelectStoreItemProvider } from '#components/organisms/modals/SelectStoreItemModalFlow.js';
import {
  useShowConsumerCampaignById,
  useShowConsumerCampaignSettingsById,
} from '@givebacks/api/store';
import { Constants } from 'core';
import { ContactOrganizerModal, EndedCampaignAlert, ShareModal } from 'core/Components';
import { useCauseParam, useCrowdFundingTranslations, usePlatformShare } from 'core/Hooks';
import { BASE_URL } from 'core/Utils';
import { Navigate, useNavigate } from 'react-router-dom';
import { RichTextDisplay } from 'rich-text';
import { Box, ButtonMantine, Container, Flex, Group, Stack, Text, Title } from 'ui';
import { KebabMenu, MobileBottomButton, SplitLayoutCard } from 'ui/Core';
import { IconShare } from 'ui/Icons';

import { CampaignCard } from '../../../components';
import classes from '../../../components/CausePageLayout.module.css';
import { HeaderImage } from '../../../components/HeaderImage';
import { Participants } from './Participants';
import { Prizes } from './Prizes';
import { Teams } from './Teams';

const CampaignShareButton = () => {
  const { causeId, campaignId } = useCrowdfundingProvider();
  const { data: campaign } = useShowConsumerCampaignById({ causeId, campaignId });

  const { data: cause } = useCauseParam();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { platformShare } = usePlatformShare();
  const navigate = useNavigate();

  if (!causeId || !campaignId || !campaign) return null;

  const participantCenterShareLink = `${BASE_URL}${Constants.appRoutes.participantCenter(
    causeId,
    campaignId
  )}`;

  return (
    <>
      <Group gap="xs">
        <ButtonMantine
          size="sm"
          variant="white"
          color="gray"
          leftSection={<IconShare />}
          onClick={() =>
            void platformShare(
              {
                share_options: {
                  url: participantCenterShareLink,
                },
              },
              () => setIsShareModalOpen(true)
            )
          }
        >
          Share
        </ButtonMantine>
        <KebabMenu
          items={[
            { label: 'Contact Organizer', onClick: () => setIsContactModalOpen(true) },
            {
              label: 'Visit Cause Page',
              onClick: () => navigate(`/causes/${cause?.uuid}`),
            },
            {
              label: 'Visit Campaign Page',
              onClick: () => navigate(`..`),
            },
            {
              label: `Share ${campaign?.participant_options.title}`,
              onClick: () => setIsShareModalOpen(true),
            },
          ]}
          buttonProps={{ variant: 'white', color: 'gray', size: 'sm' }}
        />
      </Group>

      <ShareModal
        opened={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        causeName={cause?.name}
        link={participantCenterShareLink}
        type="campaign"
        imgSrc={cause?.logo?.url}
      />
      <ContactOrganizerModal
        opened={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title="Contact Organizer"
        causeId={cause?.uuid}
        campaignId={campaign?.uuid}
      />
    </>
  );
};

export const StudentCenter = () => {
  const { causeId, campaignId } = useCrowdfundingProvider();
  const {
    data: campaign,
    isLoading,
    isError,
  } = useShowConsumerCampaignById({ causeId, campaignId });
  const { data: settings } = useShowConsumerCampaignSettingsById({
    causeId,
    campaignId,
  });

  const navigate = useNavigate();

  const { t } = useCrowdFundingTranslations(campaign?.cause_id);
  const { openModalById } = useSelectStoreItemProvider({});

  if (isError) {
    return <Navigate to={'..'} />;
  }

  return (
    <Container fluid className={classes.container}>
      <Stack className={classes.leftContent}>
        <HeaderImage
          isLoading={isLoading}
          src={
            campaign?.images?.[0]?.url ||
            'https://s3.amazonaws.com/com.givebacks.public/images/crowdfunding/campaign-default.png'
          }
          headerButtons={<CampaignShareButton />}
        >
          <Flex h={88} display={{ base: 'block', md: 'none' }}>
            <CampaignCard
              campaign_id={campaign?.uuid}
              onClick={() => navigate(`..`)}
              causeId={campaign?.cause_id}
              isColumn={false}
            />
          </Flex>
        </HeaderImage>
        {settings?.isCampaignReadOnly && <EndedCampaignAlert type="campaign" hiddenFrom="md" />}
        <Stack gap="xs">
          <Title order={1} c="gray.8" style={{ textTransform: 'capitalize' }}>
            {campaign?.participant_options.title}
          </Title>
          <Text type="body">
            <RichTextDisplay html={campaign?.participant_options.description} />
          </Text>
        </Stack>

        {settings?.isTeamsInCenterEnabled && <Teams />}
        {settings?.isParticipantsInCenterEnabled && <Participants />}
        <Prizes />
      </Stack>
      <Box className={classes.rightContent}>
        <Stack className={classes.rightContentChild}>
          <SplitLayoutCard
            topComponent={
              <CampaignCard
                campaign_id={campaign?.uuid}
                onClick={() => navigate(`..`)}
                causeId={campaign?.cause_id}
              />
            }
            bottomComponent={
              settings?.isCampaignReadOnly ? (
                <EndedCampaignAlert type="campaign" />
              ) : (
                <Group grow>
                  <ButtonMantine
                    variant="gradient"
                    onClick={() => {
                      openModalById('StoreItemDonateOptions');
                    }}
                  >
                    {t('Donate')}
                  </ButtonMantine>
                  {settings?.isRegistrationOpen && (
                    <ButtonMantine onClick={() => navigate('register')}>
                      {campaign?.participant_options.registration_call_to_action}
                    </ButtonMantine>
                  )}
                </Group>
              )
            }
          />
        </Stack>
      </Box>
      {!settings?.isCampaignReadOnly && (
        <MobileBottomButton>
          <ButtonMantine
            variant="gradient"
            onClick={() => {
              openModalById('StoreItemDonateOptions');
            }}
          >{`${t('Donate')}`}</ButtonMantine>
          {settings?.isRegistrationOpen && (
            <ButtonMantine onClick={() => navigate('register')}>
              {campaign?.participant_options.registration_call_to_action}
            </ButtonMantine>
          )}
        </MobileBottomButton>
      )}
    </Container>
  );
};
